import { useEffect, useMemo, useState } from "react";
import { Eraser, Pause, Play, RefreshCcw, ShieldAlert, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { EmptyState, PageHeader, StatusPill } from "@/components/uiext";
import { FirewallStatusCell, clearFirewallStatusCache } from "@/components/FirewallStatusCell";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

/**
 * Platform-wide Security Events feed with two clearing actions:
 *
 *  1. **Clear screen** — frontend-only. Hides every event currently older
 *     than the snapshot timestamp; new events keep streaming in. Equivalent
 *     to "give me a fresh starting point". Doesn't delete anything from
 *     the database.
 *
 *  2. **Purge log** — server-side delete via `DELETE /api/platform/security/events`.
 *     Confirmation dialog with retention presets (Everything / Older than
 *     7 / 30 / 90 days). Audit-logged.
 */
export default function PlatformSecurity() {
  const [items, setItems] = useState([]);
  const [hideBeforeTs, setHideBeforeTs] = useState(null); // ISO string
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeDays, setPurgeDays] = useState(0); // 0 = wipe everything
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/platform/security/events");
      setItems(r.data || []);
    } catch (e) {
      // silent — auto-refresh tick
    }
  };

  useEffect(() => {
    load();
    if (!autoRefresh) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [autoRefresh]);

  const visibleItems = useMemo(() => {
    if (!hideBeforeTs) return items;
    return items.filter((e) => (e.ts || "") >= hideBeforeTs);
  }, [items, hideBeforeTs]);

  const clearScreen = () => {
    const stamp = new Date().toISOString();
    setHideBeforeTs(stamp);
    toast.success("Screen cleared — new events will appear from now on.");
  };

  const restoreScreen = () => {
    setHideBeforeTs(null);
    toast.success("Screen restored — showing full history again.");
  };

  const doPurge = async () => {
    setBusy(true);
    try {
      const days = Number(purgeDays) || 0;
      const r = await api.delete(
        `/platform/security/events?older_than_days=${days}`
      );
      const n = r.data?.deleted ?? 0;
      toast.success(
        days
          ? `Purged ${n} events older than ${days} days.`
          : `Purged ${n} events (entire log wiped).`
      );
      setPurgeOpen(false);
      setHideBeforeTs(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
    } finally {
      setBusy(false);
    }
  };

  const PURGE_PRESETS = [
    { days: 0,  label: "Everything",       desc: "Wipe the entire platform-wide security log." },
    { days: 7,  label: "Older than 7 days", desc: "Keep only the most recent week." },
    { days: 30, label: "Older than 30 days", desc: "Keep only the most recent month." },
    { days: 90, label: "Older than 90 days", desc: "Keep the last quarter." },
  ];

  return (
    <div data-testid="page-platform-security">
      <PageHeader
        title="Platform Security"
        subtitle="All security events across all tenants. Use 'Clear screen' to reset the live view, or 'Purge log' to permanently delete events from the database."
        icon={ShieldAlert}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant={autoRefresh ? "default" : "outline"}
              size="sm"
              onClick={() => setAutoRefresh((v) => !v)}
              data-testid="psec-auto"
            >
              {autoRefresh ? (
                <>
                  <Pause className="h-3.5 w-3.5 mr-1" />
                  Pause auto-refresh
                </>
              ) : (
                <>
                  <Play className="h-3.5 w-3.5 mr-1" />
                  Resume auto-refresh
                </>
              )}
            </Button>
            <Button variant="outline" size="sm" onClick={load} data-testid="psec-refresh">
              <RefreshCcw className="h-3.5 w-3.5 mr-1" />
              Refresh
            </Button>
            {hideBeforeTs ? (
              <Button
                variant="outline"
                size="sm"
                onClick={restoreScreen}
                data-testid="psec-restore-screen"
              >
                <Eraser className="h-3.5 w-3.5 mr-1" />
                Restore screen
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={clearScreen}
                data-testid="psec-clear-screen"
              >
                <Eraser className="h-3.5 w-3.5 mr-1" />
                Clear screen
              </Button>
            )}
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setPurgeOpen(true)}
              data-testid="psec-purge-log"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Purge log…
            </Button>
          </div>
        }
      />

      {hideBeforeTs && (
        <div
          className="mb-3 rounded-md border border-[hsl(var(--info))]/30 bg-[hsl(var(--info))]/5 px-3 py-2 text-[11px]"
          data-testid="psec-hidden-banner"
        >
          Showing only events newer than{" "}
          <span className="font-mono">{new Date(hideBeforeTs).toLocaleString()}</span>.{" "}
          <button
            className="underline text-[hsl(var(--info))]"
            onClick={restoreScreen}
          >
            Restore full history
          </button>
        </div>
      )}

      {visibleItems.length === 0 ? (
        <EmptyState
          title={items.length === 0 ? "No events" : "Screen cleared"}
          description={
            items.length === 0
              ? "All quiet across the platform."
              : "All currently-known events were timestamped before the screen was cleared. Click 'Restore screen' or wait for new events."
          }
        />
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader className="sticky-table-head">
              <TableRow>
                <TableHead className="w-[160px]">Time</TableHead>
                <TableHead>Tenant</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Source IP</TableHead>
                <TableHead className="min-w-[220px]">Firewall</TableHead>
                <TableHead>Detail</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleItems.map((e, i) => (
                <TableRow key={`${e.ts}-${i}`} className="row-hover">
                  <TableCell className="text-xs">
                    {new Date(e.ts).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-xs font-mono">
                    {e.org_id || "—"}
                  </TableCell>
                  <TableCell>
                    <StatusPill kind="security">{e.type}</StatusPill>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{e.ip}</TableCell>
                  <TableCell>
                    {e.ip ? (
                      <FirewallStatusCell
                        ip={e.ip}
                        scope="platform"
                        onQuickBan={() => {
                          clearFirewallStatusCache(e.ip);
                          load();
                        }}
                      />
                    ) : (
                      <span className="text-[10px] text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-[11px] text-muted-foreground">
                    {JSON.stringify(e.detail)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={purgeOpen} onOpenChange={setPurgeOpen}>
        <DialogContent className="max-w-md" data-testid="psec-purge-dialog">
          <DialogHeader>
            <DialogTitle>Purge security log</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p className="text-muted-foreground">
              This permanently deletes events from the database across <b>all tenants</b>.
              The action is recorded in the platform Audit Log.
            </p>
            <div className="space-y-1.5">
              {PURGE_PRESETS.map((p) => (
                <label
                  key={p.days}
                  className={`flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer ${
                    purgeDays === p.days
                      ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10"
                      : "border-border hover:bg-muted/40"
                  }`}
                  data-testid={`psec-purge-preset-${p.days}`}
                >
                  <input
                    type="radio"
                    name="purge-preset"
                    className="mt-0.5 accent-[hsl(var(--accent))]"
                    checked={purgeDays === p.days}
                    onChange={() => setPurgeDays(p.days)}
                  />
                  <span>
                    <span className="font-medium">{p.label}</span>
                    <span className="block text-[11px] text-muted-foreground">
                      {p.desc}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setPurgeOpen(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={doPurge}
              disabled={busy}
              data-testid="psec-purge-confirm"
            >
              {busy ? "Purging…" : "Purge log"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
